import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, Loader2, AlertCircle } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };
const ROLES = [
  { value: "owner", label: "Owner" },
  { value: "admin", label: "Admin" },
  { value: "practitioner", label: "Practitioner" },
  { value: "receptionist", label: "Receptionist" },
];

const errText = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  return fallback;
};

/** Edit how a practitioner appears inside this clinic. Never touches account login. */
export const PractitionerProfileDrawer = ({ practitionerId, canManage, onClose, onSaved }) => {
  const [profile, setProfile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [fullName, setFullName] = useState("");
  const [role, setRole] = useState("");
  const [typeIds, setTypeIds] = useState([]);
  const [accepts, setAccepts] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let stale = false;
    api.get(`/practitioners/${practitionerId}/profile`)
      .then(({ data }) => {
        if (stale) return;   // a second mount must not reset edits in flight
        setProfile(data);
        setDisplayName(data.display_name || "");
        setFullName(data.account_full_name || "");
        setRole(data.role || "");
        setTypeIds(data.appointment_type_ids || []);
        setAccepts(data.accepts_appointments !== false);
      })
      .catch((err) => { if (!stale) setError(errText(err, "That practitioner could not be loaded.")); });
    return () => { stale = true; };
  }, [practitionerId]);

  const toggleType = (id) =>
    setTypeIds(typeIds.includes(id) ? typeIds.filter(t => t !== id) : [...typeIds, id]);

  const save = async () => {
    setError("");
    if (!fullName.trim()) return setError("Please enter the person's full name.");
    setSaving(true);
    try {
      await api.put(`/practitioners/${practitionerId}/profile`, {
        display_name: displayName.trim(),
        full_name: fullName.trim(),
      });
      if (canManage && role && role !== profile.role) {
        await api.put(`/team/${profile.membership_id}`, { role });
      }
      if (canManage && accepts !== (profile.accepts_appointments !== false)) {
        await api.put(`/practitioners/${practitionerId}/availability`, {
          accepts_appointments: accepts,
        });
      }
      if (canManage && accepts && JSON.stringify(typeIds) !== JSON.stringify(profile.appointment_type_ids || [])) {
        await api.put(`/availability/appointment-types/${practitionerId}`, typeIds);
      }
      onSaved?.(displayName.trim());
      onClose();
    } catch (err) {
      setError(errText(err, "Those changes could not be saved."));
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" data-testid="practitioner-profile-drawer">
      <div className="w-full max-w-md h-full bg-white overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#1A1B1A]/[0.06]">
          <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Edit practitioner</h2>
          <button onClick={onClose} data-testid="close-practitioner-drawer" className="p-1 text-[#757675] hover:text-[#1A1B1A]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!profile ? (
          <p className="p-6 text-sm text-[#757675] flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading...
          </p>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <label className="text-xs font-medium text-[#1A1B1A]">Display name</label>
              <input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                placeholder={profile.account_full_name} data-testid="practitioner-display-name-input"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <p className="text-[10px] text-[#757675] mt-1">
                How this practitioner appears in this clinic - calendar, bookings, notes, letters and
                reports. Leave blank to use the account name.
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A]">Full name</label>
              <input value={fullName} onChange={(e) => setFullName(e.target.value)}
                data-testid="practitioner-full-name-input"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]" />
              <p className="text-[10px] text-[#757675] mt-1">
                Your account profile name.
                {profile.clinic_count > 1 && (
                  <span className="text-amber-700" data-testid="multi-clinic-warning">
                    {" "}This person belongs to {profile.clinic_count} clinics - changing the full name
                    updates their account everywhere. The display name above stays specific to this clinic.
                  </span>
                )}
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A]">Email</label>
              <p className="mt-1 text-sm text-[#4A4B4A]" data-testid="practitioner-email">{profile.email}</p>
              <p className="text-[10px] text-[#757675]">Account login email</p>
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A]">Role</label>
              {canManage ? (
                <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="practitioner-role-select"
                  className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm bg-white">
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              ) : (
                <p className="mt-1 text-sm text-[#4A4B4A] capitalize">{profile.role}</p>
              )}
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A]">Practitioner status</label>
              <p className="mt-1 text-sm text-[#4A4B4A] capitalize" data-testid="practitioner-status-value">
                {profile.treats_patients
                  ? (profile.practitioner_status === "active" ? "Active practitioner" : "Former practitioner")
                  : "Not a treating practitioner"}
              </p>
              {canManage && profile.treats_patients && (
                <p className="text-[10px] text-[#757675]">
                  Change this with Deactivate / Reactivate on the Team page.
                </p>
              )}
            </div>

            {["owner", "admin", "practitioner"].includes(profile.role) && (
              <div>
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <input type="checkbox" disabled={!canManage} checked={accepts}
                    onChange={() => setAccepts(!accepts)} data-testid="accepts-appointments-toggle"
                    className="accent-[#205B5A] mt-0.5" />
                  <span>
                    <span className="text-xs font-medium text-[#1A1B1A]">Accepts appointments</span>
                    <span className="block text-[10px] text-[#757675] mt-0.5">
                      Shows them on the calendar and in booking. Turning this off keeps their
                      account, role and every past appointment exactly as they are - they simply
                      stop appearing where new bookings are made.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {accepts && (
              <div>
                <label className="text-xs font-medium text-[#1A1B1A]">Appointment types</label>
                {profile.clinic_appointment_types.length === 0 ? (
                  <p className="mt-1 text-xs text-[#757675]">No appointment types set up yet.</p>
                ) : (
                  <div className="mt-2 space-y-1.5">
                    {profile.clinic_appointment_types.map(t => (
                      <label key={t.id} className="flex items-center gap-2 text-sm text-[#4A4B4A]">
                        <input type="checkbox" disabled={!canManage} checked={typeIds.includes(t.id)}
                          onChange={() => toggleType(t.id)} data-testid={`practitioner-type-${t.id}`}
                          className="accent-[#205B5A]" />
                        {t.name} <span className="text-[10px] text-[#757675]">{t.duration_minutes} min</span>
                      </label>
                    ))}
                  </div>
                )}
                <p className="text-[10px] text-[#757675] mt-1">
                  Which services they can be booked for. Leaving all unticked means any type.
                </p>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 px-3 py-2"
                data-testid="practitioner-drawer-error">
                <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5" />
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm text-[#757675]">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="save-practitioner-profile-btn"
                className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-50">
                {saving ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
